import { useState } from "react";
import { useHotFolders, type HotFolderConfig } from "../hooks/useHotFolders";
import { useJobTypes } from "../hooks/useJobTypes";
import { EmptyState } from "@/components/EmptyState";
import { ConfirmModal } from "@/components/ConfirmModal";
import { showApiError } from "@/lib/error-toast";
import { makeDefaultSeparationConfig, type SeparationConfig } from "@capture/shared";

const COMPLETENESS_MODES = [
  { value: "sentinel", label: "Sentinel File" },
  { value: "idle", label: "Idle Timeout" },
];

const MANIFEST_MODES = [
  { value: "none", label: "None" },
  { value: "optional", label: "Optional" },
  { value: "required", label: "Required" },
];

// Slice 3a — tri-state separation override for hot folder channels.
const SEPARATION_OVERRIDE_MODES = [
  { value: "default", label: "Use job type default" },
  { value: "duplex", label: "Force on — duplex" },
  { value: "simplex", label: "Force on — simplex" },
] as const;
type SeparationOverrideMode = (typeof SEPARATION_OVERRIDE_MODES)[number]["value"];

function separationOverrideToFields(
  mode: SeparationOverrideMode,
): { separationOverride: SeparationConfig | null; duplexOverride: boolean | null } {
  if (mode === "default") return { separationOverride: null, duplexOverride: null };
  return {
    separationOverride: makeDefaultSeparationConfig(),
    duplexOverride: mode === "duplex",
  };
}

/**
 * Maps a persisted override pair back to the UI tri-state selector value.
 *
 * NOTE — this is a lossy read: the UI is preset-only (duplex / simplex at the
 * default brightness threshold), so any custom `brightnessThreshold` or
 * `keepSeparatorPages` value stored in `separationOverride` is silently
 * discarded here. If a future maintainer adds a custom-threshold UI, they must
 * reconstruct those values from `separationOverride` directly rather than
 * relying on this helper.
 */
function fieldsToSeparationOverrideMode(
  separationOverride: SeparationConfig | null,
  duplexOverride: boolean | null,
): SeparationOverrideMode {
  if (!separationOverride) return "default";
  return duplexOverride ? "duplex" : "simplex";
}

interface HotFolderFormData {
  name: string;
  watchPath: string;
  jobTypeId: string;
  pollingIntervalSec: number;
  completenessMode: string;
  sentinelFilename: string;
  idleTimeoutSec: number | null;
  isEnabled: boolean;
  routeByContent: boolean;
  manifestMode: string;
  manifestFilename: string;
  // Slice 3a — tri-state UI value; converted to separationOverride + duplexOverride on submit.
  separationOverrideMode: SeparationOverrideMode;
}

const DEFAULT_FORM: HotFolderFormData = {
  name: "",
  watchPath: "",
  jobTypeId: "",
  pollingIntervalSec: 30,
  completenessMode: "idle",
  sentinelFilename: "",
  idleTimeoutSec: 60,
  isEnabled: true,
  routeByContent: false,
  manifestMode: "none",
  manifestFilename: "",
  separationOverrideMode: "default",
};

export function HotFolderPanel() {
  const { hotFolders, isLoading, error, create, update, remove, toggle } = useHotFolders();
  const { items: jobTypes } = useJobTypes();

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<HotFolderFormData>(DEFAULT_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const setField = (key: keyof HotFolderFormData, value: unknown) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setIsCreating(true);
    try {
      const { separationOverride, duplexOverride } = separationOverrideToFields(form.separationOverrideMode);
      await create({
        name: form.name,
        watchPath: form.watchPath,
        jobTypeId: form.jobTypeId,
        pollingIntervalSec: form.pollingIntervalSec,
        completenessMode: form.completenessMode,
        sentinelFilename: form.completenessMode === "sentinel" ? form.sentinelFilename || null : null,
        idleTimeoutSec: form.completenessMode === "idle" ? form.idleTimeoutSec : null,
        isEnabled: form.isEnabled,
        routeByContent: form.routeByContent,
        manifestMode: form.manifestMode,
        manifestFilename: form.manifestMode !== "none" ? form.manifestFilename || null : null,
        separationOverride,
        duplexOverride,
      });
      setShowCreate(false);
      setForm(DEFAULT_FORM);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return <div className="admin-loading"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="panel-content">
      <div className="export-tab-header">
        <h3 className="rb-section-title">Hot Folders</h3>
        <button className="rb-btn rb-btn-save" onClick={() => setShowCreate(true)}>
          + Add Hot Folder
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {showCreate && (
        <form className="export-create-form" onSubmit={handleCreate} style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 12px", fontWeight: 600 }}>New Hot Folder</h4>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Name</label>
            <input
              className="rb-input"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Invoices Watch"
              required
            />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Watch Path</label>
            <input
              className="rb-input"
              value={form.watchPath}
              onChange={(e) => setField("watchPath", e.target.value)}
              placeholder="/mnt/shares/hot/invoices"
              required
            />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Job Type</label>
            <select
              className="rb-select"
              value={form.jobTypeId}
              onChange={(e) => setField("jobTypeId", e.target.value)}
              required
            >
              <option value="">Select job type…</option>
              {jobTypes
                .filter((jt) => jt.isActive && jt.currentPublishedVersionId)
                .map((jt) => (
                  <option key={jt.id} value={jt.id}>{jt.name}</option>
                ))}
            </select>
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Polling Interval (sec)</label>
            <input
              className="rb-input"
              type="number"
              min={5}
              max={3600}
              value={form.pollingIntervalSec}
              onChange={(e) => setField("pollingIntervalSec", Number(e.target.value))}
              required
            />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Completeness Mode</label>
            <select
              className="rb-select"
              value={form.completenessMode}
              onChange={(e) => setField("completenessMode", e.target.value)}
            >
              {COMPLETENESS_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {form.completenessMode === "sentinel" && (
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Sentinel Filename</label>
              <input
                className="rb-input"
                value={form.sentinelFilename}
                onChange={(e) => setField("sentinelFilename", e.target.value)}
                placeholder="done.flag"
              />
            </div>
          )}

          {form.completenessMode === "idle" && (
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Idle Timeout (sec)</label>
              <input
                className="rb-input"
                type="number"
                min={5}
                max={3600}
                value={form.idleTimeoutSec ?? 60}
                onChange={(e) => setField("idleTimeoutSec", Number(e.target.value))}
              />
            </div>
          )}

          <div className="rb-row" style={{ marginBottom: 10, alignItems: "center", gap: 8 }}>
            <label className="rb-label">Enabled</label>
            <input
              type="checkbox"
              checked={form.isEnabled}
              onChange={(e) => setField("isEnabled", e.target.checked)}
            />
          </div>

          {/* R-266 6a — typeless ingestion: route by document fingerprint
              instead of pinning every file to the selected job type. */}
          <div className="rb-row" style={{ marginBottom: 4, alignItems: "center", gap: 8 }}>
            <label className="rb-label" htmlFor="hf-create-route-by-content">Route by content</label>
            <input
              id="hf-create-route-by-content"
              type="checkbox"
              checked={form.routeByContent}
              onChange={(e) => setField("routeByContent", e.target.checked)}
            />
          </div>
          <p className="rb-hint" style={{ marginBottom: 10 }}>
            Route by content (no fixed job type) — auto-route every document by its fingerprint.
          </p>

          {/* R-67 S1 — manifest parsing: run a manifest file through the header pipeline. */}
          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Manifest parsing</label>
            <select
              className="rb-select"
              value={form.manifestMode}
              onChange={(e) => setField("manifestMode", e.target.value)}
            >
              {MANIFEST_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {form.manifestMode !== "none" && (
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Manifest Filename</label>
              <input
                className="rb-input"
                value={form.manifestFilename}
                onChange={(e) => setField("manifestFilename", e.target.value)}
                placeholder="manifest.xml"
              />
            </div>
          )}

          {/* Slice 3a — override document separation for this hot folder channel. */}
          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Override document separation</label>
            <select
              className="rb-select"
              value={form.separationOverrideMode}
              onChange={(e) => setField("separationOverrideMode", e.target.value as SeparationOverrideMode)}
            >
              {SEPARATION_OVERRIDE_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {createError && <div className="admin-error">{createError}</div>}

          <div className="rb-row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button type="button" className="rb-btn" onClick={() => { setShowCreate(false); setCreateError(null); }}>
              Cancel
            </button>
            <button type="submit" className="rb-btn rb-btn-save" disabled={isCreating}>
              {isCreating ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      )}

      {hotFolders.length === 0 && !showCreate && (
        // R-66: keep a single source of truth for the create affordance — the
        // panel header's "+ Add Hot Folder" button. Empty state stays as
        // explanatory text only.
        <EmptyState
          title="No hot folders configured"
          body={'Hot folders watch a directory on the server and ingest any new file dropped into it. Use “+ Add Hot Folder” above to set up automated batch creation from a network share or upload location.'}
        />
      )}

      {hotFolders.map((hf) => (
        <HotFolderCard
          key={hf.id}
          hotFolder={hf}
          jobTypes={jobTypes}
          onUpdate={update}
          onRemove={remove}
          onToggle={toggle}
        />
      ))}
    </div>
  );
}

// ─── Hot Folder Card ─────────────────────────────────────────────

function HotFolderCard({
  hotFolder,
  jobTypes,
  onUpdate,
  onRemove,
  onToggle,
}: {
  hotFolder: HotFolderConfig;
  // Full list (used to resolve the display name of the existing selection even
  // when it points at an inactive / unpublished JobType); edit dropdown filters
  // to selectable ones before rendering.
  jobTypes: Array<{ id: string; name: string; isActive?: boolean; currentPublishedVersionId?: string | null }>;
  onUpdate: (id: string, data: Partial<Omit<HotFolderConfig, "id" | "tenantId" | "createdAt" | "updatedAt">>) => Promise<HotFolderConfig>;
  onRemove: (id: string) => Promise<void>;
  onToggle: (id: string) => Promise<HotFolderConfig>;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<HotFolderFormData>({
    name: hotFolder.name,
    watchPath: hotFolder.watchPath,
    jobTypeId: hotFolder.jobTypeId,
    pollingIntervalSec: hotFolder.pollingIntervalSec,
    completenessMode: hotFolder.completenessMode,
    sentinelFilename: hotFolder.sentinelFilename ?? "",
    idleTimeoutSec: hotFolder.idleTimeoutSec,
    isEnabled: hotFolder.isEnabled,
    routeByContent: hotFolder.routeByContent ?? false,
    manifestMode: hotFolder.manifestMode ?? "none",
    manifestFilename: hotFolder.manifestFilename ?? "",
    separationOverrideMode: fieldsToSeparationOverrideMode(hotFolder.separationOverride, hotFolder.duplexOverride),
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const setEditField = (key: keyof HotFolderFormData, value: unknown) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError(null);
    setIsSaving(true);
    try {
      const { separationOverride, duplexOverride } = separationOverrideToFields(editForm.separationOverrideMode);
      await onUpdate(hotFolder.id, {
        name: editForm.name,
        watchPath: editForm.watchPath,
        jobTypeId: editForm.jobTypeId,
        pollingIntervalSec: editForm.pollingIntervalSec,
        completenessMode: editForm.completenessMode,
        sentinelFilename: editForm.completenessMode === "sentinel" ? editForm.sentinelFilename || null : null,
        idleTimeoutSec: editForm.completenessMode === "idle" ? editForm.idleTimeoutSec : null,
        routeByContent: editForm.routeByContent,
        manifestMode: editForm.manifestMode,
        manifestFilename: editForm.manifestMode !== "none" ? editForm.manifestFilename || null : null,
        separationOverride,
        duplexOverride,
      });
      setIsEditing(false);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await onToggle(hotFolder.id);
    } catch (e) {
      showApiError(e, { fallback: "Toggle failed" });
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDeleteOpen(false);
    try {
      await onRemove(hotFolder.id);
    } catch (e) {
      showApiError(e, { fallback: "Delete failed" });
    }
  };

  const jobTypeName = jobTypes.find((j) => j.id === hotFolder.jobTypeId)?.name ?? hotFolder.jobTypeId;

  return (
    <div className={`export-dest-card ${!hotFolder.isEnabled ? "export-dest-card--inactive" : ""}`}>
      <div className="export-dest-header" onClick={() => setIsExpanded((v) => !v)}>
        <div className="export-dest-info">
          <span className="export-dest-name">{hotFolder.name}</span>
          <span className={`jt-badge ${hotFolder.isEnabled ? "jt-badge--active" : "jt-badge--inactive"}`}>
            {hotFolder.isEnabled ? "Watching" : "Paused"}
          </span>
          <span className="export-dest-type">{hotFolder.watchPath}</span>
        </div>
        <div className="export-dest-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="rb-btn"
            onClick={handleToggle}
            disabled={isToggling}
          >
            {hotFolder.isEnabled ? "Pause" : "Resume"}
          </button>
          <button className="rb-btn" onClick={() => { setIsEditing(true); setIsExpanded(true); }}>
            Edit
          </button>
          <button className="rb-btn rb-btn-danger" onClick={() => setConfirmDeleteOpen(true)}>
            Delete
          </button>
        </div>
      </div>

      <ConfirmModal
        open={confirmDeleteOpen}
        title={`Delete hot folder "${hotFolder.name}"?`}
        description="This stops the watch and removes the configuration. The folder on disk is untouched."
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {isExpanded && !isEditing && (
        <div className="export-dest-detail">
          <div className="detail-grid">
            <span className="detail-label">Job Type</span><span>{hotFolder.routeByContent ? "Route by content" : jobTypeName}</span>
            <span className="detail-label">Mode</span><span>{COMPLETENESS_MODES.find((m) => m.value === hotFolder.completenessMode)?.label ?? hotFolder.completenessMode}</span>
            <span className="detail-label">Poll</span><span>{hotFolder.pollingIntervalSec}s</span>
            {hotFolder.sentinelFilename && <><span className="detail-label">Sentinel</span><span>{hotFolder.sentinelFilename}</span></>}
            {hotFolder.idleTimeoutSec && <><span className="detail-label">Idle Timeout</span><span>{hotFolder.idleTimeoutSec}s</span></>}
            {hotFolder.manifestMode && hotFolder.manifestMode !== "none" && (
              <>
                <span className="detail-label">Manifest</span>
                <span>{MANIFEST_MODES.find((m) => m.value === hotFolder.manifestMode)?.label ?? hotFolder.manifestMode}{hotFolder.manifestFilename ? ` · ${hotFolder.manifestFilename}` : ""}</span>
              </>
            )}
            {hotFolder.separationOverride && (
              <>
                <span className="detail-label">Separation</span>
                <span>{hotFolder.duplexOverride ? "Forced — duplex" : "Forced — simplex"}</span>
              </>
            )}
          </div>
        </div>
      )}

      {isExpanded && isEditing && (
        <div className="export-dest-detail">
          <form onSubmit={handleSave}>
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Name</label>
              <input className="rb-input" value={editForm.name} onChange={(e) => setEditField("name", e.target.value)} required />
            </div>
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Watch Path</label>
              <input className="rb-input" value={editForm.watchPath} onChange={(e) => setEditField("watchPath", e.target.value)} required />
            </div>
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Job Type</label>
              <select className="rb-select" value={editForm.jobTypeId} onChange={(e) => setEditField("jobTypeId", e.target.value)} required>
                <option value="">Select…</option>
                {jobTypes
                  .filter((jt) => jt.isActive !== false && jt.currentPublishedVersionId)
                  .map((jt) => <option key={jt.id} value={jt.id}>{jt.name}</option>)}
              </select>
            </div>
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Polling (sec)</label>
              <input className="rb-input" type="number" min={5} max={3600} value={editForm.pollingIntervalSec} onChange={(e) => setEditField("pollingIntervalSec", Number(e.target.value))} />
            </div>
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Mode</label>
              <select className="rb-select" value={editForm.completenessMode} onChange={(e) => setEditField("completenessMode", e.target.value)}>
                {COMPLETENESS_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            {editForm.completenessMode === "sentinel" && (
              <div className="rb-row" style={{ marginBottom: 10 }}>
                <label className="rb-label">Sentinel File</label>
                <input className="rb-input" value={editForm.sentinelFilename} onChange={(e) => setEditField("sentinelFilename", e.target.value)} placeholder="done.flag" />
              </div>
            )}
            {editForm.completenessMode === "idle" && (
              <div className="rb-row" style={{ marginBottom: 10 }}>
                <label className="rb-label">Idle (sec)</label>
                <input className="rb-input" type="number" min={5} max={3600} value={editForm.idleTimeoutSec ?? 60} onChange={(e) => setEditField("idleTimeoutSec", Number(e.target.value))} />
              </div>
            )}
            {/* R-266 6a — typeless ingestion toggle (see create form). */}
            <div className="rb-row" style={{ marginBottom: 4, alignItems: "center", gap: 8 }}>
              <label className="rb-label" htmlFor={`hf-edit-route-by-content-${hotFolder.id}`}>Route by content</label>
              <input
                id={`hf-edit-route-by-content-${hotFolder.id}`}
                type="checkbox"
                checked={editForm.routeByContent}
                onChange={(e) => setEditField("routeByContent", e.target.checked)}
              />
            </div>
            <p className="rb-hint" style={{ marginBottom: 10 }}>
              Route by content (no fixed job type) — auto-route every document by its fingerprint.
            </p>
            {/* R-67 S1 — manifest parsing (see create form). */}
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Manifest parsing</label>
              <select className="rb-select" value={editForm.manifestMode} onChange={(e) => setEditField("manifestMode", e.target.value)}>
                {MANIFEST_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            {editForm.manifestMode !== "none" && (
              <div className="rb-row" style={{ marginBottom: 10 }}>
                <label className="rb-label">Manifest Filename</label>
                <input className="rb-input" value={editForm.manifestFilename} onChange={(e) => setEditField("manifestFilename", e.target.value)} placeholder="manifest.xml" />
              </div>
            )}
            {/* Slice 3a — override document separation for this hot folder channel. */}
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Override document separation</label>
              <select
                className="rb-select"
                value={editForm.separationOverrideMode}
                onChange={(e) => setEditField("separationOverrideMode", e.target.value as SeparationOverrideMode)}
              >
                {SEPARATION_OVERRIDE_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            {editError && <div className="admin-error">{editError}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
              <button type="button" className="rb-btn" onClick={() => setIsEditing(false)}>Cancel</button>
              <button type="submit" className="rb-btn rb-btn-save" disabled={isSaving}>{isSaving ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
